import Vue from "vue";
import VueRouter, { RouteConfig, NavigationGuard, Route, RawLocation } from "vue-router";
import Store from "@/store/store";
import Dirtyness from "@/utilities/Dirtyness";

Vue.use(VueRouter as any); // eslint-disable-line @typescript-eslint/no-explicit-any

let intendedUrl: string | null = null;

// call this in the *component* code (not here in the router setup)
export function beforeRouteLeave(to: Route, from: Route, next: (to?: string | void | false) => void) {
    if(Dirtyness.isDirty && !confirm("Changes may not be saved.  Click cancel to stay on this page.")) {
        return next(false);
    }
    Dirtyness.reset();
    return next();
}

const beforeEnterGuard: NavigationGuard = async (to: Route, from: Route, next: (to?: string | void) => void) => {

    //console.log(`### nav guard... signed in=${store.getters.isSignedIn} intendedUrl=${intendedUrl}`);

    if (Store.getters.isSignedIn) {
        if (intendedUrl) {
            const url = intendedUrl;
            intendedUrl = null;
            //console.log("### nav guard - now logged in - redirect");
            return next(url); // goto stored url
        } else {
            //console.log("### nav guard - carry on...");
            return next(); // all is fine
        }
    }

    try {
        // get user from session storage
        await Store.dispatch("loadSignedInUser");
    } catch (e) {
        console.error(e);
    }

    if (Store.getters.isSignedIn) {
        //console.log("### nav guard - got login ok");
        next();
    } else {
        intendedUrl = to.path; // store entry url before redirect
        //console.log("### nav guard - redirect to signin");
        next("/signIn");
    }
};

//
// -- set up the routes
//

const routes: Array<RouteConfig> = [];

routes.push({ path: "/", name: "Home", meta: { layout: "visitor" }, component: () => import("@/vue/views/SiteList.vue") });

routes.push({ path: "/reset/:key", name: "ResetPassword", meta: { layout: "blank" }, component: () => import("@/vue/views/ResetPassword.vue") });
routes.push({ path: "/activate/:key", name: "Activate", meta: { layout: "blank" }, component: () => import("@/vue/views/ResetPassword.vue") });
routes.push({ path: "/signin", name: "SignIn", meta: { layout: "visitor" }, component: () => import("@/vue/views/SignIn.vue") });
routes.push({ path: "/error", name: "Error500", meta: { layout: "visitor" }, component: () => import("@/vue/views/Error500.vue") });
routes.push({ path: "/contact", name: "Contact", meta: { layout: "visitor" }, component: () => import("@/vue/views/Contact.vue") });
routes.push({ path: "/qr/:key", name: "QrCodeVisitList", meta: { layout: "visitor" }, component: () => import("@/vue/views/QrCodeVisitList.vue") });
routes.push({ path: "/publicApi", name: "PublicApi", meta: { layout: "visitor" }, component: () => import("@/vue/views/PublicApi.vue") });

routes.push({ path: "/siteList", name: "VisitorSiteList", meta: { layout: "visitor" }, component: () => import("@/vue/views/SiteList.vue") });
routes.push({ path: "/siteRegister/:siteID", name: "VisitorSiteRegister", meta: { layout: "visitor" }, component: () => import("@/vue/views/SiteRegister.vue") });
routes.push({ path: "/briefing/:visitID/:pageID?", name: "Briefing", meta: { layout: "visitor" }, component: () => import("@/vue/views/Briefing.vue") });
routes.push({ path: "/briefingDone/:visitID", name: "BriefingDone", meta: { layout: "visitor" }, component: () => import("@/vue/views/BriefingDone.vue") });
routes.push({ path: "/briefingPrint/:visitID", name: "BriefingPrint", meta: { layout: "blank" }, component: () => import("@/vue/views/BriefingPrint.vue") });
routes.push({ path: "/workPermit/:workPermitId", name: "WorkPermit", meta: { layout: "visitor" }, component: () => import("@/vue/views/WorkPermit.vue") });

// this is just to catch old links (new route is workPermit above) - and can be removed in future
routes.push({ path: "/signWorkPermit/:key", name: "SignWorkPermit", meta: { layout: "visitor" }, component: () => import("@/vue/views/SignWorkPermit.vue") });

routes.push({ path: "/admin", name: "Admin", meta: { layout: "admin" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/Home.vue") });
routes.push({ path: "/admin/Home", name: "AdminHome", meta: { layout: "admin" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/Home.vue") });
routes.push({ path: "/admin/Profile", name: "AdminProfile", meta: { layout: "admin" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/Profile.vue") });
routes.push({ path: "/admin/users", name: "AdminUsers", meta: { layout: "admin" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/Users.vue") });
routes.push({ path: "/admin/clients", name: "AdminClients", meta: { layout: "admin" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/Clients.vue") });
routes.push({ path: "/admin/sites", name: "AdminSites", meta: { layout: "admin" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/Sites.vue") });
routes.push({ path: "/admin/site/:siteID", name: "AdminSite", meta: { layout: "admin" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/Site.vue") });
routes.push({ path: "/admin/visits", name: "AdminVisits", meta: { layout: "admin" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/Visits.vue") });
routes.push({ path: "/admin/visitPrint/:visitID", name: "AdminVisitPrint", meta: { layout: "blank" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/VisitPrint.vue") });
routes.push({ path: "/admin/resources", name: "AdminResources", meta: { layout: "admin" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/Resources.vue") });
routes.push({ path: "/admin/briefings", name: "AdminBriefings", meta: { layout: "admin" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/Briefings.vue") });
routes.push({ path: "/admin/briefing/:briefingID", name: "AdminBriefing", meta: { layout: "admin" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/Briefing.vue") });
routes.push({ path: "/admin/questionReport", name: "QuestionReport", meta: { layout: "admin" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/QuestionReport.vue") });
routes.push({ path: "/admin/visitReport", name: "VisitReport", meta: { layout: "admin" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/VisitReport.vue") });
routes.push({ path: "/admin/workPermits", name: "AdminWorkPermits", meta: { layout: "admin" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/WorkPermits.vue") });
routes.push({ path: "/admin/workPermit/:workPermitID", name: "AdminWorkPermit", meta: { layout: "admin" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/WorkPermit.vue") });
routes.push({ path: "/admin/emailLog", name: "EmailLog", meta: { layout: "admin" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/EmailLog.vue") });
routes.push({ path: "/admin/lookups/:group", name: "Lookups", meta: { layout: "admin" }, beforeEnter: beforeEnterGuard, component: () => import("@/vue/views/Admin/Lookups.vue") });

// This needs to be the **last** route
routes.push({ path: "*", meta: { layout: "visitor" }, component: () => import("@/vue/views/Error404.vue") });

//
// -- create the router
//

const router = new VueRouter({
    mode: "history",
    linkActiveClass: "active",
    linkExactActiveClass: "active",
    base: process.env.BASE_URL,
    routes
});

export function goToIntendedUrl() {
    if (!intendedUrl) router.replace("/admin/home"); // default in case not defined (?)
    router.replace(intendedUrl as RawLocation);
}

export default router;
